import type { RefObject } from "react";
import { Icon } from "../../icons";
import type { CommandItem } from "./commandItem";
import { commandOptionId, groupHeadingAt } from "./paletteModel";

interface CommandOptionProps {
  id: string;
  item: CommandItem;
  index: number;
  active: boolean;
  onActivate: (index: number) => void;
  onRun: (item: CommandItem) => void;
}

function CommandOption({ id, item, index, active, onActivate, onRun }: CommandOptionProps): React.JSX.Element {
  return (
    <div
      id={id}
      role="option"
      aria-selected={active}
      data-index={index}
      className={`nb-palette-item${active ? " is-active" : ""}`}
      onMouseMove={() => onActivate(index)}
      onClick={() => onRun(item)}
    >
      <span className="nb-palette-icon">
        <Icon name={item.icon ?? "arrowRight"} />
      </span>
      <span className="nb-truncate">{item.label}</span>
      {item.hint ? <span className="nb-palette-hint">{item.hint}</span> : null}
      <Icon name="arrowRight" className="nb-palette-enter" />
    </div>
  );
}

function NoResults({ query }: { query: string }): React.JSX.Element {
  return (
    <div className="nb-palette-empty">
      <Icon name="search" />
      <p>{`No results for "${query.trim()}"`}</p>
    </div>
  );
}

interface CommandPaletteResultsProps {
  listId: string;
  listRef: RefObject<HTMLDivElement | null>;
  results: CommandItem[];
  query: string;
  active: number;
  onActivate: (index: number) => void;
  onRun: (item: CommandItem) => void;
}

/** The results listbox: options under their group headings, or an empty state. */
export function CommandPaletteResults({ listId, listRef, results, query, active, onActivate, onRun }: CommandPaletteResultsProps): React.JSX.Element {
  return (
    <div className="nb-palette-list" id={listId} role="listbox" ref={listRef} aria-label="Results">
      {results.length === 0 ? (
        <NoResults query={query} />
      ) : (
        results.map((item, index) => {
          const heading = groupHeadingAt(results, index, query);
          return (
            <div key={item.id} role="presentation">
              {heading ? (
                <div className="nb-palette-group" role="presentation">
                  {heading}
                </div>
              ) : null}
              <CommandOption
                id={commandOptionId(listId, item)}
                item={item}
                index={index}
                active={index === active}
                onActivate={onActivate}
                onRun={onRun}
              />
            </div>
          );
        })
      )}
    </div>
  );
}
